import { bytesToNDigits } from '@mail/cross-sizes-utils';
import sha1 from 'js-sha1';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_BLOCKED } from 'reactApp/appHelpers/configHelpers';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useActionsConfig } from 'reactApp/hooks/useActionsConfig';
import { openAttachLetter } from 'reactApp/modules/attaches/attaches.helpers';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { resetSelect, selectOne } from 'reactApp/modules/selections/selections.actions';
import { changeStorageHistory, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { formatFileInfoDate } from 'reactApp/utils/formatDate';
import { sendXray } from 'reactApp/utils/ga';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import { fileInfoItems } from './useItems.data';
import { closeFileInfoModal, closeViewerModal, getFileDirection, getFileName, getShareAccess } from './useItems.helpers';
import { UseItemsHook } from './useItems.types';

const {
    filename,
    uploadDate,
    size: sizeInfo,
    direction: directionInfo,
    share,
    removeDate,
    originalPlace,
    letter,
    author,
    letterTitle,
} = fileInfoItems;

export const useItems: UseItemsHook = ({ id, storage }) => {
    const [fullName, setFullName] = useState(false);
    const dispatch = useDispatch();
    const availableActions = useActionsConfig({ id, isMobile: true, storage });
    const isPublishEnabled = availableActions[EActions.publish];
    const { isTrashBin, isPublic, isGallery, isAttaches } = getStorage(storage);
    const {
        ext,
        mtime,
        nameWithoutExt,
        name,
        weblink,
        size,
        deletedAt,
        deletedFrom,
        home,
        isFolder,
        authorEmail,
        authorName,
        authorAvatar,
        folderId = '',
        message_id = '',
        attachType,
        subject,
        homeId,
    } = useSelector((state: RootState) => getStorageItemById(state, storage, id)) as any;
    const attachClonedItem = useSelector((state: RootState) => homeId && getStorageItemById(state, EStorageType.home, homeId));

    const path = isTrashBin ? deletedFrom : home;
    const direction = path ? path.split('/')?.filter(Boolean) : undefined;
    const directionWithoutLast = direction?.slice(0, direction.length - 1);

    const folderName = useSelector((state: RootState) => AttachesSelectors.getAttachFolderById(state, folderId))?.name;

    const isRootDir = path === ROOT_FOLDER_ID;

    const hasUploadDate = !isTrashBin && !isFolder && !!mtime;
    const hasShare = !isRootDir && !!isPublishEnabled && (!isAttaches || attachType !== EAttachTypes.cloud) && !IS_BLOCKED;
    const hasDirection = !isTrashBin && !isRootDir && direction;
    const hasWeblink = Boolean(isAttaches ? attachClonedItem?.weblink : weblink);

    const onSelect = useCallback(() => {
        const selectedIdx = isTrashBin ? path : `/${directionWithoutLast?.join('/')}`;
        changeStorageHistory({
            id: selectedIdx,
            ...(isPublic || isGallery || isTrashBin ? { navigateToStorage: EStorageType.home } : { storage }),
        });
        closeFileInfoModal();
        closeViewerModal();
        sendPaymentGa({
            eventCategory: 'viewer',
            action: 'view_in_folder',
            source: storage,
            id_media: sha1(id),
            id_public: isPublic ? getWeblinkFromPublicId(id) : 'None',
        });
    }, [directionWithoutLast, isGallery, isPublic, isTrashBin, path, storage]);

    const onOpenLetter = useCallback(() => {
        if (attachType === EAttachTypes.temporary) {
            return;
        }

        sendXray(['fv', 'attachinfo', 'click'], null, { sendImmediately: true });

        openAttachLetter({ folderId, message_id });
    }, [folderId, message_id, attachType]);

    const onShare = useCallback(() => {
        dispatch(selectOne({ selectedIdx: id, storage }));
        toolbarActions.publish('mobile-file-info', id, storage);
        closeFileInfoModal();
        dispatch(resetSelect());
    }, [dispatch, id, storage]);

    const toggleFullName = useCallback(() => {
        setFullName((value) => !value);
    }, []);

    const fileInfoItemsRecord = {
        filename: {
            ...filename,
            rightIcon: fullName ? 'up' : 'down',
            value: getFileName(fullName ? name : nameWithoutExt || name, fullName ? '' : ext, fullName),
            onClick: toggleFullName,
        },
        letterTitle: isAttaches
            ? {
                  ...letterTitle,
                  value: subject || '<Без темы>',
              }
            : null,
        author: isAttaches
            ? {
                  ...author,
                  value: authorName || authorEmail,
                  Icon: authorAvatar ? () => <Avatar size={28} email={authorEmail} name={authorName} /> : author.Icon,
              }
            : null,
        uploadDate: hasUploadDate && {
            ...uploadDate,
            value: formatFileInfoDate(mtime),
            section: isAttaches ? 'Дата получения' : uploadDate.section,
        },
        removeDate: isTrashBin && { ...removeDate, value: formatFileInfoDate(deletedAt) },
        size: size && { ...sizeInfo, value: bytesToNDigits(size, 3).value },
        share: hasShare && { ...share, active: Boolean(hasWeblink), value: getShareAccess(hasWeblink), onClick: onShare },
        direction: hasDirection && { ...directionInfo, value: getFileDirection(directionWithoutLast), onClick: onSelect },
        originalPlace: isTrashBin && { ...originalPlace, value: getFileDirection(direction), onClick: onSelect },
        letter: isAttaches ? { ...letter, onClick: onOpenLetter, value: folderName } : null,
    };

    const header = isAttaches ? 'Информация о вложении' : `Информация о ${isFolder ? 'папке' : 'файле'}`;
    return {
        header,
        items: Object.values(fileInfoItemsRecord).filter(Boolean),
    };
};
